import React, { useEffect, useState } from 'react';
import { Copy, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../../config'; 

const ReferralScreen = ({ onBack, referralInfo: initialReferralInfo, referralsUsed: initialReferralsUsed }) => {
  const [referralInfo, setReferralInfo] = useState(initialReferralInfo);
  const [referralsUsed, setReferralsUsed] = useState(initialReferralsUsed || []);
  const [loading, setLoading] = useState(!initialReferralInfo);

  const fetchReferralInfo = async () => {
    try {
      const userinfo = JSON.parse(localStorage.getItem('fitscan_user'));
      const Username = userinfo?.username;
      const Gymcode = userinfo?.gymcode;
      if (!Username || !Gymcode) return;

      const res = await axios.post(`${API_BASE_URL}/api/Referral/user/referral`, {
        username: Username,
        gymcode: Gymcode,
      },
        {
          headers: {
            'Authorization': `Bearer ${JSON.parse(localStorage.getItem("accessToken") || '{}').token || ''}`
          }
        }
      );

      let referral = res.data;

      if (!referral) {
        const createRes = await axios.post(`${API_BASE_URL}/api/Referral/create`, {
          username: Username,
          gymcode: Gymcode,
        },
          {
            headers: {
              'Authorization': `Bearer ${JSON.parse(localStorage.getItem("accessToken") || '{}').token || ''}`
            }
          }
        );
        referral = createRes.data;
      }
      setReferralInfo(referral);
      setReferralsUsed(referral.referralStatuses || []);
    } catch (err) {
      console.error("Error fetching referral info:", err);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(referralInfo.referralLink);
      alert('Referral link copied to clipboard!');
    } catch {
      alert('Failed to copy. Please try manually.');
    }
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join FitScan with my referral!',
          text: 'Use my code for extra benefits!',
          url: referralInfo.referralLink,
        });
      } catch (err) {
        console.error('Sharing failed:', err);
      }
    } else {
      alert('Sharing not supported. Copy link manually.');
    }
  };

  useEffect(() => {
    if (!referralInfo) {
      fetchReferralInfo();
    }
  }, [referralInfo]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-lime-400"></div>
        <p className="text-gray-400 animate-pulse">Loading referral details...</p>
      </div>
    );
  }

  const isDataReady = referralInfo && referralInfo.referralCode;

  return (
    <div className="max-w-[480px] mx-auto pt-4 pb-8 text-white">
      {/* Top Bar */}
      <div className="flex items-center gap-3 mb-6">
        <button 
          className="p-2 bg-[#222] border border-[#2b2b2b] hover:bg-[#2b2b2b] rounded-lg text-gray-400 hover:text-white transition-all" 
          onClick={onBack}
        >
          <ArrowLeft size={18} />
        </button>
        <span className="text-[20px] font-medium text-white">Referral Program</span>
      </div>

      <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl p-6 mb-6">
        <p className="text-lg mb-2">Your Referral Code:</p>
        <div className="flex items-center justify-between bg-[#222] p-4 rounded-lg">
          <span className="font-mono text-xl text-lime-400">
            {isDataReady ? referralInfo.referralCode : '--------'}
          </span>
          <button 
            onClick={copyToClipboard} 
            disabled={!isDataReady}
            className={`text-lime-400 hover:text-lime-300 transition-opacity ${!isDataReady ? 'opacity-30 cursor-not-allowed' : ''}`}
          >
            <Copy className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-400 mt-3">
          Share this with friends to invite them and get exclusive rewards!
        </p>

        <button
          onClick={shareLink}
          disabled={!isDataReady}
          className={`mt-6 w-full bg-lime-400 text-black px-4 py-3 rounded-lg font-bold transition-all ${
            !isDataReady ? 'opacity-50 grayscale cursor-not-allowed' : 'hover:bg-lime-500 active:scale-[0.98]'
          }`}
        >
          Share Referral Link
        </button>
      </div>

      <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl p-6">
        <h2 className="text-xl font-semibold mb-4">Your Referral Status</h2>
        {!referralsUsed || referralsUsed.length === 0 ? (
          <div className="text-center py-6 text-gray-500 bg-[#1a1a1a] rounded-lg">
            <p className="text-sm">No one has used your referral code yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-left text-gray-300">
              <thead>
                <tr className="border-b border-[#2b2b2b]">
                  <th className="px-4 py-3 font-medium">Friend</th>
                  <th className="px-4 py-3 font-medium">Joined</th>
                  <th className="px-4 py-3 font-medium">Reward</th>
                </tr>
              </thead>
              <tbody>
                {referralsUsed.map((ref, index) => (
                  <tr key={index} className="border-b border-[#2b2b2b] hover:bg-[#2b2b2b]/50 transition-colors">
                    <td className="px-4 py-3">{ref.friendName}</td>
                    <td className="px-4 py-3">{new Date(ref.joinedDate).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-lime-400">{ref.reward}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralScreen;
